import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import AccessGroupPermissionsPanel from "components/accessGroups/AccessGroupPermissionsPanel";
import { pickerOrder } from "components/accessGroups/accessStepModel";
import type { AccessCatalog, AccessGroup } from "types/interfaces/access";
import StepHeading from "./StepHeading";

interface Props {
  catalog: AccessCatalog | undefined;
  groups: AccessGroup[] | undefined;
  selectedGroupId: number | null;
  onSelectGroup: (id: number) => void;
  canChooseGroup: boolean;
  /** Why a group may not be given here, by group id; groups left out may be. */
  refusals?: Record<number, string>;
  aiAgent: boolean;
  onAiAgentChange: (on: boolean) => void;
  canChangeAiAgent: boolean;
}

/** Step 2 of the internal user form: the access group and the AI Agent switch. */
export default function UserAccessStep({
  catalog,
  groups,
  selectedGroupId,
  onSelectGroup,
  canChooseGroup,
  refusals = {},
  aiAgent,
  onAiAgentChange,
  canChangeAiAgent,
}: Props) {
  const selected = groups?.find((g) => g.id === selectedGroupId) ?? null;

  return (
    <Box>
      <StepHeading
        title="User Access"
        subtitle="Set the user's access group. This determines what the user will see and do on the web platform."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box>
          {!groups ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />
            ))
          ) : (
            <RadioGroup
              aria-label="Access group"
              value={selectedGroupId == null ? "" : String(selectedGroupId)}
              onChange={(e) => onSelectGroup(Number(e.target.value))}
            >
              {pickerOrder(groups).map((group) => {
                const checked = group.id === selectedGroupId;
                // Shown only where the group could otherwise be chosen.
                const refusal = canChooseGroup ? refusals[group.id] : undefined;
                return (
                  <Box
                    key={group.id}
                    sx={{
                      mb: 1,
                      px: 1.5,
                      border: "1px solid",
                      borderColor: checked ? "primary.main" : "divider",
                      borderRadius: 1,
                      bgcolor: checked
                        ? "calibrate.primarySoft10"
                        : refusal
                          ? "grey.50"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value={String(group.id)}
                      control={<Radio size="small" />}
                      disabled={!canChooseGroup || Boolean(refusal && !checked)}
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: refusal ? "text.disabled" : "primary.main",
                            }}
                          >
                            {group.name}
                          </Typography>
                          {refusal && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="div"
                            >
                              {refusal}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        width: "100%",
                        m: 0,
                        py: 0.5,
                        alignItems: "flex-start",
                        "& .MuiRadio-root": { mt: "-2px" },
                      }}
                    />
                  </Box>
                );
              })}
            </RadioGroup>
          )}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {catalog && selected ? (
            <AccessGroupPermissionsPanel
              catalog={catalog}
              tiers={selected.tiers}
              subHeader={selected.sub_header}
              appliesTo={selected.applies_to}
              aiAgent={aiAgent}
            />
          ) : (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "grey.50",
                p: 2,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: 18, color: "primary.main" }}
              >
                Permissions Granted
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a group to see what the user will be able to do. Without
                a group the user reaches no page.
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>AI Agent</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ lineHeight: 1.4 }}
              >
                Toggle this switch to enable the set up for the AI Agent. You
                will need a Chat ID and API Key.
              </Typography>
            </Box>
            <Switch
              checked={aiAgent}
              disabled={!canChangeAiAgent}
              onChange={(e) => onAiAgentChange(e.target.checked)}
              inputProps={{ "aria-label": "AI Agent" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
